import { observer } from "mobx-react-lite";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTran } from "@/hooks";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatValue } from "../../../domains/formatting";
import { SCOPE_KEYS, SCOPE_LABELS } from "../../../domains/scope";
import { dashboardEChartsStore } from "../../../stores/e-charts.store";
import { buildScopeDescriptionCopy } from "../../../utils/e-charts-view";
import { colorForIndex, SCOPE_COLOR } from "./colors";
import SortedTooltipContent from "./SortedTooltipContent";
import { stableAxisKey, stableAxisMax } from "./stable-y-axis";
import { buildSortedTooltipRows } from "./tooltip-rows";

const CONSOLIDATED_KEY = "value";
const HIDDEN_TOOLTIP_WRAPPER_STYLE = {
  display: "none",
};
const TOOLTIP_OFFSET = 10;
const TOOLTIP_VIEWPORT_PADDING = 8;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const ScopeChartPortalTooltip = ({
  active,
  coordinate,
  label,
  payload,
  chartHostRef,
  consolidated,
  entityNameById,
  scopeKey,
  // The tooltip portals to document.body, so the stale-view veil's opacity
  // and inert cannot reach it; without this flag a tooltip left open when a
  // refetch starts (pointer parked on a bar) would float full-strength above
  // the dimmed chart until the pointer moves.
  veiled,
  t,
}) => {
  const tooltipRef = useRef(null);
  const [hostRect, setHostRect] = useState(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  // Same sorted share-of-total rows as the hero tooltip, with the share
  // computed within this scope for the year (R09 item 4). Consolidated mode
  // keeps the single scope line without a percentage.
  const { rows, totalLabel } = useMemo(() => {
    if (consolidated) {
      const item = (payload ?? [])[0];
      if (!item) return { rows: [], totalLabel: null };
      return {
        rows: [
          {
            key: CONSOLIDATED_KEY,
            name: t(SCOPE_LABELS[scopeKey]),
            color: item.color || item.fill,
            valueLabel: formatValue(Number(item.value)),
          },
        ],
        totalLabel: null,
      };
    }
    const sorted = buildSortedTooltipRows(payload, (item) => {
      const id = String(item.dataKey ?? item.name);
      return entityNameById.get(id) ?? id;
    });
    return {
      rows: sorted.rows,
      totalLabel: `${t(SCOPE_LABELS[scopeKey])}: ${formatValue(sorted.sum)}`,
    };
  }, [consolidated, entityNameById, payload, scopeKey, t]);

  useLayoutEffect(() => {
    if (!active || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    const nextSize = {
      width: Math.ceil(rect.width),
      height: Math.ceil(rect.height),
    };
    setTooltipSize((prev) =>
      prev.width === nextSize.width && prev.height === nextSize.height
        ? prev
        : nextSize,
    );
    // hostRect: the portal first mounts on the render hostRect resolves, so
    // the measure must re-run then or the first paint skips viewport clamping.
  }, [active, label, rows, hostRect]);

  useLayoutEffect(() => {
    if (!active) return;

    const updateHostRect = () => {
      const nextRect = chartHostRef.current?.getBoundingClientRect();
      if (!nextRect) {
        setHostRect(null);
        return;
      }

      const nextValue = {
        left: nextRect.left,
        top: nextRect.top,
        width: nextRect.width,
        height: nextRect.height,
      };
      setHostRect((prev) =>
        prev &&
        prev.left === nextValue.left &&
        prev.top === nextValue.top &&
        prev.width === nextValue.width &&
        prev.height === nextValue.height
          ? prev
          : nextValue,
      );
    };

    updateHostRect();
    window.addEventListener("resize", updateHostRect);
    window.addEventListener("scroll", updateHostRect, true);

    return () => {
      window.removeEventListener("resize", updateHostRect);
      window.removeEventListener("scroll", updateHostRect, true);
    };
  }, [active, chartHostRef, coordinate]);

  if (
    typeof document === "undefined" ||
    veiled ||
    !active ||
    !coordinate ||
    rows.length === 0 ||
    !hostRect
  ) {
    return null;
  }

  const rawLeft = hostRect.left + coordinate.x + TOOLTIP_OFFSET;
  const rawTop = hostRect.top + coordinate.y + TOOLTIP_OFFSET;
  const maxLeft =
    window.innerWidth - tooltipSize.width - TOOLTIP_VIEWPORT_PADDING;
  const maxTop =
    window.innerHeight - tooltipSize.height - TOOLTIP_VIEWPORT_PADDING;
  const left =
    tooltipSize.width > 0
      ? clamp(
          rawLeft,
          TOOLTIP_VIEWPORT_PADDING,
          Math.max(TOOLTIP_VIEWPORT_PADDING, maxLeft),
        )
      : rawLeft;
  const top =
    tooltipSize.height > 0
      ? clamp(
          rawTop,
          TOOLTIP_VIEWPORT_PADDING,
          Math.max(TOOLTIP_VIEWPORT_PADDING, maxTop),
        )
      : rawTop;

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: "fixed",
        left,
        top,
        zIndex: 30,
        // Never a hit-test target: a box that overlaps the cursor would steal
        // the pointer from the chart and flicker the tooltip on/off. The box
        // no longer scrolls routinely, so it does not need pointer events.
        pointerEvents: "none",
      }}
    >
      <SortedTooltipContent
        label={label}
        totalLabel={totalLabel}
        rows={rows}
        rankTop={!consolidated}
      />
    </div>,
    document.body,
  );
};

const ScopeChart = observer(
  ({
    scopeKey,
    entities,
    years,
    entityNameById,
    entityColorById,
    // From the panel, not the store: while a refetch is pending these are the
    // dimension and drill of the view the ROWS answer, so the caption swaps
    // together with the data, never ahead of it.
    displayedDimension,
    axisContextKey,
    drillContext,
    refreshing,
  }) => {
    const { t } = useTran();
    const store = dashboardEChartsStore;
    const consolidated = store.consolidated;
    const chartHostRef = useRef(null);

    // Coerce missing cells to 0 — same rationale as EChartsHero: keep the
    // entity present in the tooltip even when it has no data for the year.
    const data = years.map((year) => {
      const row = { year };
      if (consolidated) {
        row[CONSOLIDATED_KEY] =
          store.consolidatedValueFor(year, scopeKey, entities) ?? 0;
      } else {
        entities.forEach((id) => {
          row[id] = store.valueFor(id, year, scopeKey) ?? 0;
        });
      }
      return row;
    });

    // Pinned Y scale, same standing rule and registry as the hero: entity
    // ticking and drilling never move this axis; only genuinely larger data
    // raises it, and only a filter context rotation resets it.
    const dataMax = data.reduce((max, row) => {
      const values = consolidated
        ? [row[CONSOLIDATED_KEY]]
        : entities.map((id) => row[id]);
      return values.reduce(
        (m, v) => (Number.isFinite(v) && v > m ? v : m),
        max,
      );
    }, 0);
    const yAxisMax = stableAxisMax(
      stableAxisKey({
        contextKey: axisContextKey,
        chartId: scopeKey,
        dimension: displayedDimension,
        consolidated,
      }),
      dataMax,
    );
    const yAxisDomain = yAxisMax == null ? undefined : [0, yAxisMax];

    const entityCount = entities.length;
    // Sub-chart bars carry no drill handler and no affordance in this build:
    // the hero is the only trigger (brief section 1). Only the caption follows
    // the drill so all four charts tell the same story.
    const descriptionCopy = buildScopeDescriptionCopy({
      displayedDimension,
      drillContext,
      consolidated,
      entityCount,
    });
    const description = t(descriptionCopy.key, descriptionCopy.params);
    return (
      <Card className="flex w-full flex-col rounded-xl border-border/70 bg-card/95 shadow-sm p-0">
        <CardHeader className="border-b px-3 py-2.5">
          <CardTitle className="text-sm font-bold text-foreground">
            {t(SCOPE_LABELS[scopeKey])}{" "}
            <span className="text-[11px] font-normal text-muted-foreground">
              tCO₂e
            </span>
          </CardTitle>
          <CardDescription className="text-[11px] font-normal text-muted-foreground">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3">
          <div ref={chartHostRef}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data} barGap={4}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={yAxisDomain}
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  content={
                    <ScopeChartPortalTooltip
                      chartHostRef={chartHostRef}
                      consolidated={consolidated}
                      entityNameById={entityNameById}
                      scopeKey={scopeKey}
                      veiled={refreshing}
                      t={t}
                    />
                  }
                  cursor={{ fill: "hsl(var(--muted))" }}
                  wrapperStyle={HIDDEN_TOOLTIP_WRAPPER_STYLE}
                />
                {/* isAnimationActive false on all bars: data swaps happen in
                    mounted charts under the stale-view veil, and a growth
                    animation on every swap re-introduces exactly the motion
                    the veil removes (brief section 3 point 7). */}
                {consolidated ? (
                  <Bar
                    key={CONSOLIDATED_KEY}
                    dataKey={CONSOLIDATED_KEY}
                    fill={SCOPE_COLOR[scopeKey]}
                    isAnimationActive={false}
                    radius={[2, 2, 0, 0]}
                    barSize={15}
                  />
                ) : (
                  entities.map((id, idx) => (
                    <Bar
                      key={id}
                      dataKey={id}
                      fill={
                        entityColorById?.get(String(id)) ?? colorForIndex(idx)
                      }
                      isAnimationActive={false}
                      radius={[2, 2, 0, 0]}
                      barSize={15}
                    />
                  ))
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    );
  },
);

const EChartsSupportingGrid = ({
  entities,
  years,
  entityNameById,
  entityColorById,
  displayedDimension,
  axisContextKey,
  drillContext,
  refreshing,
}) => {
  // No mount gate on empty selections (standing no-jump rule): the three
  // frames keep their place and height while an empty state is being
  // explained above them, drawing empty plots on their pinned axes instead
  // of collapsing the page by three cards.
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {SCOPE_KEYS.map((scopeKey) => (
        <ScopeChart
          key={scopeKey}
          scopeKey={scopeKey}
          entities={entities}
          years={years}
          entityNameById={entityNameById}
          entityColorById={entityColorById}
          displayedDimension={displayedDimension}
          axisContextKey={axisContextKey}
          drillContext={drillContext}
          refreshing={refreshing}
        />
      ))}
    </div>
  );
};

export default EChartsSupportingGrid;
